import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store/store';
import { cancelWithdrawal, fetchWithdrawalHistory } from '@/store/slices/walletSlice';
import { TransactionStatusBadge } from '@/components/wallet';
import { formatCurrencyAuto, formatDate } from '@/utils/formatters';
import { WithdrawalRequest, WithdrawalStatus } from '@/types/wallet';
import { FiArrowLeft, FiChevronLeft, FiChevronRight, FiRefreshCw, FiXCircle } from 'react-icons/fi';

const STATUS_OPTIONS: Array<{ value: 'all' | WithdrawalStatus; label: string }> = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'approved', label: 'Approved' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const WithdrawalsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { withdrawals, loading, actionLoading } = useSelector((state: RootState) => state.wallet);

  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all' | WithdrawalStatus>('all');

  useEffect(() => {
    dispatch(fetchWithdrawalHistory({ page: currentPage, limit: 20 }));
  }, [dispatch, currentPage]);

  const filteredWithdrawals = useMemo(() => {
    const list = withdrawals?.withdrawals || [];
    if (statusFilter === 'all') {
      return list;
    }
    return list.filter((item) => item.status === statusFilter);
  }, [withdrawals, statusFilter]);

  const totalPages = withdrawals?.pagination?.totalPages || 1;
  const totalItems = withdrawals?.pagination?.totalItems || 0;

  const canCancelWithdrawal = (item: WithdrawalRequest) => {
    return item.status === 'pending' || item.status === 'reviewing';
  };

  const getMethodLabel = (item: WithdrawalRequest) => {
    const method = item.withdrawalMethod;
    if (method.type === 'bank_transfer') {
      return `${method.bankName || 'Bank Transfer'} • ${method.accountNumber}`;
    }
    if (method.type === 'mobile_money') {
      return `${(method.provider || 'Mobile Money').toUpperCase()} • ${method.accountNumber}`;
    }
    return `Paystack Transfer • ${method.accountNumber}`;
  };

  const handleRefresh = () => {
    dispatch(fetchWithdrawalHistory({ page: currentPage, limit: 20 }));
  };

  const handleCancel = async (item: WithdrawalRequest) => {
    const confirmed = window.confirm(`Cancel withdrawal request ${item.requestId}?`);
    if (!confirmed) {
      return;
    }

    await dispatch(cancelWithdrawal(item._id));
    dispatch(fetchWithdrawalHistory({ page: currentPage, limit: 20 }));
  };

  return (
    <>
      <Helmet>
        <title>Withdrawal History - SideGO Wallet</title>
      </Helmet>

      <div className="relative py-1 sm:py-2">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-gradient-to-b from-cyan-100/60 to-transparent dark:from-cyan-900/20" />

        <div className="mb-3 sm:mb-4">
          <button
            onClick={() => navigate('/dashboard/wallet')}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back to Wallet
          </button>
        </div>

        <div className="mb-4 overflow-hidden rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm backdrop-blur dark:border-gray-800 dark:bg-gray-900/85 sm:mb-8 sm:p-6">
          <div className="mb-3 flex items-center justify-between gap-2 sm:mb-4">
            <span className="inline-flex items-center whitespace-nowrap rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              Withdrawal Ledger
            </span>
            <span className="inline-flex items-center whitespace-nowrap rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700 dark:border-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300">
              Request Tracking
            </span>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 dark:text-white sm:text-3xl">Withdrawal History</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-gray-400 sm:text-base">
            Monitor, review, and manage all your withdrawal requests
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="border-b border-slate-200 p-3 dark:border-gray-800 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="min-w-0 flex-1 sm:max-w-xs">
                <select
                  aria-label="Filter withdrawals by status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'all' | WithdrawalStatus)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleRefresh}
                className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <FiRefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>

          <div className="space-y-3 p-3 sm:p-5">
            {loading ? (
              <div className="py-12 text-center text-sm text-slate-500 dark:text-gray-400">Loading withdrawals...</div>
            ) : filteredWithdrawals.length === 0 ? (
              <div className="py-12 text-center text-sm text-slate-500 dark:text-gray-400">
                No withdrawals found for the selected filter.
              </div>
            ) : (
              filteredWithdrawals.map((item) => (
                <div
                  key={item._id}
                  className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/40 p-3 dark:border-gray-800 dark:bg-gray-900/40 sm:flex-row sm:items-start sm:justify-between sm:p-4"
                >
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white sm:text-base">{item.requestId}</h3>
                      <TransactionStatusBadge status={item.status} size="sm" />
                    </div>
                    <p className="text-xs text-slate-600 dark:text-gray-400 sm:text-sm">{getMethodLabel(item)}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-gray-500 sm:text-xs">
                      <span>Requested: {formatDate(String(item.requestedAt || item.createdAt))}</span>
                      <span>Fee: {formatCurrencyAuto(item.fee, item.currency, undefined, true)}</span>
                    </div>
                  </div>

                  <div className="sm:text-right">
                    <p className="text-base font-bold text-slate-900 dark:text-white sm:text-lg">
                      {formatCurrencyAuto(item.netAmount, item.currency, undefined, true)}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-gray-500 sm:text-xs">
                      Requested: {formatCurrencyAuto(item.amount, item.currency, undefined, true)}
                    </p>

                    {canCancelWithdrawal(item) && (
                      <button
                        onClick={() => handleCancel(item)}
                        disabled={actionLoading}
                        className="mt-2 inline-flex items-center gap-1 rounded-lg border border-red-300 px-2.5 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/20"
                      >
                        <FiXCircle className="h-3.5 w-3.5" />
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {withdrawals?.pagination && withdrawals.pagination.totalPages > 1 && (
            <div className="border-t border-slate-200 p-3 dark:border-gray-800 sm:p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-slate-600 dark:text-gray-400 sm:text-sm">
                  Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, totalItems)} of {totalItems} withdrawals
                </p>

                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="rounded-xl border border-slate-300 px-3 py-2 text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                    aria-label="Previous page"
                  >
                    <FiChevronLeft />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum = i + 1;
                      if (totalPages > 5) {
                        if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`rounded-xl px-3 py-2 text-sm ${
                            currentPage === pageNum
                              ? 'bg-primary-600 text-white'
                              : 'border border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="rounded-xl border border-slate-300 px-3 py-2 text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                    aria-label="Next page"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default WithdrawalsPage;
